import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Megaphone, ImageOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { listAds, saveAd, removeAd, errMsg } from "@/lib/api";
import { PageHeader, LocalBanner } from "@/components/common";
import { cn } from "@/lib/utils";

const EMPTY = { title: "", subtitle: "", image: "", cta: "Order now", active: true };
const SYNC_NOTE = "Saved to your dashboard account (real backend). These appear in the customer app once your team enables the ads feed.";

export default function Ads() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["ads"], queryFn: async () => (await listAds()).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const ads = Array.isArray(data) ? data : [];

  const saveMut = useMutation({
    mutationFn: (b) => saveAd(b),
    onSuccess: () => { toast.success("Banner saved"); qc.invalidateQueries({ queryKey: ["ads"] }); setOpen(false); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const delMut = useMutation({
    mutationFn: (id) => removeAd(id),
    onSuccess: () => { toast.success("Banner removed"); qc.invalidateQueries({ queryKey: ["ads"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const submit = () => {
    if (!form.title.trim()) return toast.error("Title is required");
    saveMut.mutate({
      ...(form.id ? { id: form.id } : {}),
      title: form.title.trim(), subtitle: form.subtitle, image: form.image.trim(),
      cta: form.cta || "Order now", active: form.active,
    });
  };
  const openEdit = (a) => { setForm({ id: a.id, title: a.title, subtitle: a.subtitle || "", image: a.image || "", cta: a.cta || "Order now", active: a.active }); setOpen(true); };
  const toggle = (a) => saveMut.mutate({ ...a, active: !a.active });

  return (
    <div className="p-5 sm:p-8 max-w-4xl">
      <PageHeader
        title="Ads &amp; Banners"
        subtitle="Promotional banners for your storefront"
        action={
          <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="add-ad-btn" className="font-semibold">
            <Plus className="h-4 w-4 mr-1.5" /> New banner
          </Button>
        }
      />
      <LocalBanner className="mb-6" text={SYNC_NOTE} />

      {isLoading ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : ads.length === 0 ? (
        <div className="rounded-md border border-dashed border-border py-16 text-center text-muted-foreground">
          No banners yet. Create a promo banner to feature your best dishes.
        </div>
      ) : (
        <div className="space-y-4">
          {ads.map((a) => (
            <div key={a.id} className={cn("rounded-lg border overflow-hidden relative", a.active ? "border-border" : "border-border opacity-60")} data-testid={`ad-${a.id}`}>
              <button onClick={() => openEdit(a)} className="block w-full text-left" data-testid={`ad-edit-${a.id}`}>
                <div className="relative h-40 bg-gradient-to-br from-primary to-emerald-700">
                  {a.image ? (
                    <img src={a.image} alt={a.title} className="absolute inset-0 h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : null}
                  <div className="absolute inset-0 bg-black/35" />
                  <div className="relative h-full flex flex-col justify-center p-6 text-white">
                    <p className="font-display font-black text-2xl leading-tight">{a.title}</p>
                    {a.subtitle && <p className="text-white/85 mt-1">{a.subtitle}</p>}
                    <span className="mt-3 inline-flex w-fit items-center bg-white text-primary text-sm font-semibold rounded-full px-3.5 py-1.5">{a.cta}</span>
                  </div>
                </div>
              </button>
              <div className="flex items-center justify-between px-4 py-2.5 bg-card">
                <div className="flex items-center gap-2 text-sm">
                  <Megaphone className="h-4 w-4 text-primary" />
                  <span className={cn("font-medium", a.active ? "text-emerald-600" : "text-muted-foreground")}>{a.active ? "Live" : "Paused"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={a.active} onCheckedChange={() => toggle(a)} data-testid={`ad-toggle-${a.id}`} />
                  <button onClick={() => delMut.mutate(a.id)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`ad-delete-${a.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="ad-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{form.id ? "Edit banner" : "New banner"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Headline</Label>
              <Input data-testid="ad-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Flat 30% off this weekend" />
            </div>
            <div className="space-y-1.5">
              <Label>Subtitle</Label>
              <Input data-testid="ad-subtitle" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="On all biryani orders" />
            </div>
            <div className="space-y-1.5">
              <Label>Image URL (optional)</Label>
              <Input data-testid="ad-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://..." />
              {form.image ? null : (
                <p className="text-xs text-muted-foreground flex items-center gap-1"><ImageOff className="h-3 w-3" /> Falls back to a green gradient.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Button text</Label>
              <Input data-testid="ad-cta" value={form.cta} onChange={(e) => setForm({ ...form, cta: e.target.value })} placeholder="Order now" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saveMut.isPending} data-testid="save-ad-btn" className="font-semibold">
              {saveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save banner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
